/**
 * Čisté, immutable operace nad stromem layoutu (id-klíčované, structural-sharing — jen cesta
 * root→změněný uzel dostane nové identity → memoizovaný renderer překresluje min. podstrom).
 * Per-kind rekonstrukce zachovává meta (grid colSpan/rowStart, tab id/label). Testované.
 */

import { childrenOf, type ContainerNode, collectIds, type GridNode, type LayoutDir, type LayoutNode } from "@globals/layouts/schema";

export const uid = (): string =>
  typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `n${Math.round(Math.random() * 1e9)}-${Date.now() % 1e6}`;

/** Nahradí přímé děti uzlu (rekonstrukce per kind, zachová meta). `next` dostane sjednocené děti.
 *  U gridu/tabs: pro existující id zachová wrapper META (colSpan/rowStart, resp. tab id/label),
 *  ale použije NOVÝ (upravený) uzel `n` — jinak by se každá editace uvnitř gridu/tabu zahodila. */
const withChildren = (node: LayoutNode, next: LayoutNode[]): LayoutNode => {
  if (node.kind === "container") return { ...node, children: next };
  if (node.kind === "grid") {
    const byId = new Map(node.children.map((c) => [c.node.id, c]));
    return { ...node, children: next.map((n) => { const o = byId.get(n.id); return o ? { ...o, node: n } : { node: n, colSpan: 1 }; }) };
  }
  if (node.kind === "tabs") {
    const byId = new Map(node.tabs.map((t) => [t.child.id, t]));
    return { ...node, tabs: next.map((n) => { const o = byId.get(n.id); return o ? { ...o, child: n } : { id: uid(), label: "Tab", child: n }; }) };
  }
  return node; // widget — bez dětí
};

/** Mapuje každý PŘÍMÝ dětský podstrom přes fn (zachová meta). null = dítě se odstraní. */
const mapChildSubtrees = (node: LayoutNode, fn: (child: LayoutNode) => LayoutNode | null): LayoutNode => {
  const next: LayoutNode[] = [];
  for (const c of childrenOf(node)) {
    const r = fn(c);
    if (r) next.push(r);
  }
  return withChildren(node, next);
};

/** Najde uzel + jeho rodiče + index (v pořadí childrenOf). */
export const findNode = (
  root: LayoutNode,
  id: string
): { node: LayoutNode; parent: LayoutNode | null; index: number } | null => {
  if (root.id === id) return { node: root, parent: null, index: -1 };
  const kids = childrenOf(root);
  for (let i = 0; i < kids.length; i++) {
    if (kids[i].id === id) return { node: kids[i], parent: root, index: i };
    const deep = findNode(kids[i], id);
    if (deep) return deep;
  }
  return null;
};

/** Cesta id od rootu k uzlu (včetně), nebo null. */
export const findPath = (root: LayoutNode, id: string, acc: string[] = []): string[] | null => {
  const path = [...acc, root.id];
  if (root.id === id) return path;
  for (const c of childrenOf(root)) {
    const r = findPath(c, id, path);
    if (r) return r;
  }
  return null;
};

/** Hloubkově nahradí uzel s daným id výsledkem replaceru. */
export const replaceNode = (root: LayoutNode, id: string, replacer: (n: LayoutNode) => LayoutNode): LayoutNode => {
  if (root.id === id) return replacer(root);
  return mapChildSubtrees(root, (c) => replaceNode(c, id, replacer));
};

/** Hloubkově upraví props uzlu (mělký merge; patch musí odpovídat kind). */
export const updateNode = (root: LayoutNode, id: string, patch: Partial<LayoutNode>): LayoutNode =>
  replaceNode(root, id, (n) => ({ ...n, ...patch }) as LayoutNode);

/** Odstraní uzel (a u tabs i jeho tab). Root nelze odstranit. */
export const removeNode = (root: LayoutNode, id: string): LayoutNode => {
  if (root.id === id) return root; // root chráněn
  return mapChildSubtrees(root, (c) => (c.id === id ? null : removeNode(c, id)));
};

/** Vloží `node` do kontejneru/gridu `parentId` na index. (tabs se needituje přes insert.) */
export const insertNode = (root: LayoutNode, parentId: string, index: number, node: LayoutNode): LayoutNode =>
  replaceNode(root, parentId, (p) => {
    if (p.kind === "container") {
      const next = [...p.children];
      next.splice(Math.max(0, Math.min(index, next.length)), 0, node);
      return { ...p, children: next };
    }
    if (p.kind === "grid") {
      const next = [...p.children];
      next.splice(Math.max(0, Math.min(index, next.length)), 0, { node, colSpan: 1 });
      return { ...p, children: next };
    }
    return p; // widget/tabs — insert nepodporován genericky
  });

/** Přesun uzlu pod nového rodiče na index. Odmítne přesun do vlastního potomka (cyklus). */
export const moveNode = (root: LayoutNode, nodeId: string, newParentId: string, index: number): LayoutNode => {
  if (nodeId === newParentId) return root;
  const found = findNode(root, nodeId);
  if (!found) return root;
  if (collectIds(found.node).includes(newParentId)) return root; // nelze do vlastního podstromu
  const without = removeNode(root, nodeId);
  return insertNode(without, newParentId, index, found.node);
};

/** Hluboký klon s NOVÝMI id (pro duplicate / reset). */
export const regenerateIds = (node: LayoutNode): LayoutNode => {
  const cloned: LayoutNode = { ...node, id: uid() } as LayoutNode;
  if (cloned.kind === "container") cloned.children = node.kind === "container" ? node.children.map(regenerateIds) : [];
  else if (cloned.kind === "grid")
    cloned.children = node.kind === "grid" ? node.children.map((c) => ({ ...c, node: regenerateIds(c.node) })) : [];
  else if (cloned.kind === "tabs")
    cloned.tabs = node.kind === "tabs" ? node.tabs.map((t) => ({ ...t, id: uid(), child: regenerateIds(t.child) })) : [];
  return cloned;
};

/** Zduplikuje uzel a vloží kopii hned za originál (stejný rodič). */
export const duplicateNode = (root: LayoutNode, id: string): LayoutNode => {
  const found = findNode(root, id);
  if (!found || !found.parent) return root;
  const copy = regenerateIds(found.node);
  return insertNode(root, found.parent.id, found.index + 1, copy);
};

/** Nová prázdná mřížka (statická tabulka) s daným počtem sloupců. */
export const makeGrid = (cols = 2): GridNode => ({ kind: "grid", id: uid(), cols, rowGap: 8, colGap: 8, children: [] });

/** Upraví meta dítěte v mřížce (colSpan / rowStart) — wrapper `{node,colSpan,rowStart}` zůstává zachován. */
export const updateGridChild = (root: LayoutNode, childId: string, patch: { colSpan?: number; rowStart?: number }): LayoutNode => {
  const rec = (n: LayoutNode): LayoutNode => {
    if (n.kind === "grid")
      return { ...n, children: n.children.map((c) => (c.node.id === childId ? { ...c, ...patch, node: rec(c.node) } : { ...c, node: rec(c.node) })) };
    if (n.kind === "container") return { ...n, children: n.children.map(rec) };
    if (n.kind === "tabs") return { ...n, tabs: n.tabs.map((t) => ({ ...t, child: rec(t.child) })) };
    return n;
  };
  return rec(root);
};

/** Zabalí uzel do nového kontejneru daného směru (na jeho místě). */
export const wrapInContainer = (root: LayoutNode, id: string, dir: LayoutDir): LayoutNode =>
  replaceNode(root, id, (n) => {
    const container: ContainerNode = {
      kind: "container",
      id: uid(),
      dir,
      gap: 8,
      padding: 0,
      align: "stretch",
      justify: "start",
      wrap: false,
      children: [n],
      size: n.size,
    };
    // přenes size na wrapper, vnitřnímu nech grow:1 ať vyplní
    return { ...container, children: [{ ...n, size: { grow: 1 } } as LayoutNode] };
  });

/**
 * Úklid stromu: zahodí prázdné NE-root kontejnery/gridy, sbalí kontejner s jediným dítětem do
 * rodičovského slotu (přenese size). Root zůstává container. Idempotentní — voláno po každé
 * strukturální editaci a před uložením.
 */
export const normalize = (root: LayoutNode): LayoutNode => {
  const visit = (n: LayoutNode, isRoot: boolean): LayoutNode | null => {
    if (n.kind === "widget") return n;
    // nejdřív normalizuj děti
    const nn = mapChildSubtrees(n, (c) => visit(c, false));
    const kids = childrenOf(nn);
    if (!isRoot && (nn.kind === "container" || nn.kind === "grid")) {
      if (kids.length === 0) return null; // prázdný → pryč
      if (nn.kind === "container" && kids.length === 1) {
        // sbal: nahraď tímto dítětem, přenes size kontejneru pokud dítě vlastní nemá
        const only = kids[0];
        return { ...only, size: only.size ?? nn.size } as LayoutNode;
      }
    }
    return nn;
  };
  const out = visit(root, true);
  return out ?? root;
};
